// import SearchBox from "@/common/SearchBox"; // 사업장 이미지는 검색 기능이 없으므로 제거
import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import {
  useBusinessCompanyPhotoList,
  // useBusinessCompanyPhotoDetail, // 현재는 사용하지 않음
  useBusinessCompanyPhotoDelete,
  useBusinessCompanyPhotoSetRepresentative,
  useBusinessCompanyPhotoOrder,
  useBusinessCompanyPhotoSaveMedia,
} from "~/services/business/companyPhoto";
import {
  canUseResourceBridge,
  uploadResourceViaBridge,
} from "%/appResourceUpload";
import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ImageViewer } from "@/ImageViewer";
import { Dialog } from "@/Dialog";

export const Route = createFileRoute(
  "/business/layout/user-business-photo-management"
)({
  component: BusinessPhotoManagement,
});

function BusinessPhotoManagement() {
  const { history } = useRouter();
  const queryClient = useQueryClient();


  // 검색 키워드 상태 (사업장 이미지는 검색 기능이 없으므로 제거)
  // const [searchKeyword, setSearchKeyword] = useState<string>("");
  // const [currentSearchKeyword, setCurrentSearchKeyword] = useState<string>("");
  // const [isSearchTriggered, setIsSearchTriggered] = useState<boolean>(false);

  // Dialog 상태
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");

  // 삭제 확인 Dialog 상태
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);

  // 이미지 뷰어 상태
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedCompanyPhotoSeq, setSelectedCompanyPhotoSeq] = useState<
    number | null
  >(null);
  const [isImageViewerVisible, setIsImageViewerVisible] = useState(false);
  const [selectedImagesUrl, setSelectedImagesUrl] = useState<string | null>(
    null
  );
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  // API 호출 (사업장 이미지 목록 조회)
  const {
    data: photoListData,
    isLoading,
    error,
  } = useBusinessCompanyPhotoList();

  // DELETE API 훅
  const deletePhotoMutation = useBusinessCompanyPhotoDelete();
  // 대표 지정 / 순서 저장 훅
  const setRepMutation = useBusinessCompanyPhotoSetRepresentative();
  const orderMutation = useBusinessCompanyPhotoOrder();

  const refetchPhotos = () =>
    queryClient.invalidateQueries({ queryKey: ["businessCompanyPhotoList"] });

  // 대표 사진 지정
  const handleSetRepresentative = (companyPhotoSeq: number) => {
    setRepMutation.mutate(companyPhotoSeq, { onSuccess: refetchPhotos });
  };

  // 순서 이동 (dir: -1 앞으로 / +1 뒤로)
  const handleMovePhoto = (index: number, dir: number) => {
    const list = photoListData?.data || [];
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    const seqs = list.map((p) => p.companyPhotoSeq);
    [seqs[index], seqs[j]] = [seqs[j], seqs[index]];
    orderMutation.mutate(seqs, { onSuccess: refetchPhotos });
  };

  // 사진·영상 추가 — 앱은 네이티브 미디어 피커(사진+영상), 각각 타입 판별해 매장 미디어로 등록
  const saveMediaMutation = useBusinessCompanyPhotoSaveMedia();
  const [mediaUploading, setMediaUploading] = useState(false);

  const isVideoUrl = (url: string | undefined | null) => {
    if (!url) return false;
    const u = url.toLowerCase();
    return [".mp4", ".mov", ".m4v", ".webm", ".avi", ".mkv", ".3gp"].some((e) =>
      u.includes(e)
    );
  };

  const handleAddMedia = async () => {
    // 앱 브릿지가 없으면(웹) 기존 사진 업로드 화면으로
    if (!canUseResourceBridge()) {
      history.push("/business/layout/company-photo-add");
      return;
    }
    try {
      setMediaUploading(true);
      // 사진+영상 통합 피커(원본 업로드) → fileSeq
      const r = await uploadResourceViaBridge(
        "/api/files/upload?fileType=FILE_TYPE_WORKPLACE_PHOTO",
        "media"
      );
      const type = isVideoUrl(r.fileUrl) ? "VIDEO" : "IMAGE";
      await saveMediaMutation.mutateAsync({
        fileSeq: r.fileSeq,
        cdMediaType: type,
      });
      refetchPhotos();
      setDialogTitle("등록 완료");
      setDialogMessage(
        type === "VIDEO" ? "홍보 영상이 등록되었습니다." : "사진이 등록되었습니다."
      );
      setIsDialogVisible(true);
    } catch (e) {
      setDialogTitle("업로드 실패");
      setDialogMessage(
        e instanceof Error ? e.message : "업로드에 실패했습니다."
      );
      setIsDialogVisible(true);
    } finally {
      setMediaUploading(false);
    }
  };

  // DETAIL API 훅 (ImageViewer에서 사용) - 현재는 사용하지 않음
  // const detailPhotoMutation = useBusinessCompanyPhotoDetail();

  const userAgent = navigator.userAgent;
  const isIOS = useMemo(() => {
    return /MT_I/i.test(userAgent);
  }, [userAgent]);
  const isAndroid = useMemo(() => {
    return /MT_A/i.test(userAgent);
  }, [userAgent]);

  console.log(photoListData);

  // 컴포넌트 마운트 시 목록 재호출
  useEffect(() => {
    // 사업장 이미지 목록 쿼리 무효화하여 재호출
    queryClient.invalidateQueries({
      queryKey: ["businessCompanyPhotoList"],
    });
  }, [queryClient]);

  // 이미지 뷰어 닫기
  const closeImageViewer = () => {
    setIsImageViewerVisible(false);
    setSelectedImage(null);
    setSelectedCompanyPhotoSeq(null);
  };

  // 사업장 이미지 관리 전용 뒤로가기 처리 함수를 전역에 등록
  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isAndroidApp = /MT_A/i.test(userAgent);

    if (isAndroidApp && typeof window !== "undefined") {
      // 커스텀 뒤로가기 처리 함수 (ImageViewer 열려있으면 닫기)
      window.customBackHandler = () => {
        console.log("business photo management 커스텀 뒤로가기 처리");
        if (isImageViewerVisible) {
          closeImageViewer();
        } else {
          // 서브 페이지이므로 뒤로가기
          console.log("서브 페이지 - history.back() 호출");
          window.history.back();
        }
      };

      console.log("business photo management customBackHandler 등록");

      // 컴포넌트 언마운트 시 정리
      return () => {
        console.log("business photo management 언마운트 - customBackHandler 제거");
        window.customBackHandler = null;
      };
    }
  }, [isImageViewerVisible]);

  // Dialog 닫기 함수
  const handleDialogClose = () => {
    setIsDialogVisible(false);
    setIsImageViewerVisible(false);
    setDialogMessage("");
    setDialogTitle("");
  };

  // 삭제 확인 Dialog 닫기 함수
  const handleDeleteConfirmClose = () => {
    setIsDeleteConfirmVisible(false);
  };

  // 삭제 확인 Dialog에서 삭제 실행 함수
  const handleDeleteConfirm = () => {
    setIsDeleteConfirmVisible(false);

    if (!selectedCompanyPhotoSeq) {
      console.error("삭제할 이미지의 companyPhotoSeq가 없습니다.");
      setDialogTitle("삭제 실패");
      setDialogMessage("삭제할 이미지 정보를 찾을 수 없습니다.");
      setIsDialogVisible(true);
      return;
    }

    deletePhotoMutation.mutate(
      { companyPhotoSeq: selectedCompanyPhotoSeq },
      {
        onSuccess: (response) => {
          console.log("사진 삭제 성공:", response);
          // 성공 시 갤러리 목록 새로고침
          queryClient.invalidateQueries({
            queryKey: ["businessCompanyPhotoList"],
          });
          closeImageViewer();
          setDialogTitle("삭제 성공");
          setDialogMessage("사진이 성공적으로 삭제되었습니다.");
          setIsDialogVisible(true);
        },
        onError: (error) => {
          console.error("사진 삭제 실패:", error);
          setDialogTitle("삭제 실패");
          setDialogMessage("사진 삭제에 실패했습니다. 다시 시도해주세요.");
          setIsDialogVisible(true);
        },
      }
    );
  };

  // 개별 이미지 선택 - 이미지 뷰어 팝업 띄우기
  const toggleImageSelection = (image: {
    url: string;
    companyPhotoSeq?: number;
  }) => {
    // 모든 이미지 URL과 현재 인덱스 찾기
    const allImages = photoListData?.data || [];
    const currentIndex = allImages.findIndex(
      (img) => img.companyPhotoSeq === image.companyPhotoSeq
    );

    setSelectedImage(image.url);
    setSelectedCompanyPhotoSeq(image.companyPhotoSeq || null);
    setSelectedImagesUrl(image.url);
    setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
    setIsImageViewerVisible(true);
  };

  // 이미지 다운로드
  const handleImageDownload = () => {
    if (!selectedCompanyPhotoSeq || !selectedImagesUrl) {
      console.error("다운로드할 이미지의 companyPhotoSeq가 없습니다.");
      setDialogTitle("다운로드 실패");
      setDialogMessage("다운로드할 이미지 정보를 찾을 수 없습니다.");
      setIsDialogVisible(true);
      return;
    }
    if (isAndroid) {
      window.mung.downloadImage(selectedImagesUrl);
    } else if (isIOS) {
      // iOS 개발자 요구사항에 맞춰 수정
      window.webkit?.messageHandlers?.mung?.postMessage([
        "downloadImage",
        selectedImagesUrl,
      ]);
    } else {
      // 웹에서는 직접 이미지 URL을 다운로드
      const link = document.createElement("a");
      link.href = selectedImagesUrl;
      link.download = `company-image-${currentImageIndex + 1}-${Date.now()}.jpg`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 이미지 삭제
  const handleImageDelete = () => {
    if (!selectedCompanyPhotoSeq) {
      console.error("삭제할 이미지의 companyPhotoSeq가 없습니다.");
      setDialogTitle("삭제 실패");
      setDialogMessage("삭제할 이미지 정보를 찾을 수 없습니다.");
      setIsDialogVisible(true);
      return;
    }

    // 삭제 확인 Dialog 표시
    setIsDeleteConfirmVisible(true);
  };

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <>
        <LayoutHeader
          title={"사업장 이미지 관리"}
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5] items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <>
        <LayoutHeader
          title={"사업장 이미지 관리"}
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5] items-center justify-center">
          <div className="text-red-500">
            데이터를 불러오는 중 오류가 발생했습니다.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!selectedImage && (
        <LayoutHeader title={"사업장 이미지 관리"} hasBackBtn hasFootPrint={false} />
      )}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5]">
        {/* 헤더 */}
        <div className="flex justify-end items-center px-4 py-3">
          <div className="flex gap-2">
            <button
              onClick={handleAddMedia}
              disabled={mediaUploading}
              className="text-gray-600 disabled:opacity-50"
            >
              {mediaUploading ? "업로드 중…" : "+ 사진·영상 추가"}
            </button>
          </div>
        </div>

        {/* 갤러리 목록 */}
        <div className="overflow-auto flex-1">
          <div className="flex flex-col gap-6 p-4">
            {/* API 데이터가 있으면 표시 */}
            {photoListData?.data && photoListData.data.length > 0 ? (
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-4">
                {photoListData.data.map((item, imageIndex) => {
                  const isRep = item.representativeYn === "Y";
                  const isVideo = item.cdMediaType === "VIDEO";
                  return (
                  <div
                    key={imageIndex}
                    className="relative aspect-square"
                  >
                    {isVideo ? (
                      // 영상: 썸네일(있으면) + 재생 아이콘, 없으면 회색 플레이스홀더
                      <div
                        onClick={() =>
                          toggleImageSelection({
                            url: item.fileUrl,
                            companyPhotoSeq: item.companyPhotoSeq,
                          })
                        }
                        className="relative w-full h-full rounded-lg bg-gray-800 cursor-pointer overflow-hidden"
                      >
                        {item.thumbnailUrl && (
                          <img
                            src={item.thumbnailUrl}
                            alt={`Company video ${imageIndex + 1}`}
                            className="object-cover w-full h-full opacity-90"
                          />
                        )}
                        <span className="absolute inset-0 flex items-center justify-center text-white text-lg">
                          ▶
                        </span>
                      </div>
                    ) : (
                    <img
                      src={item.fileUrl}
                      alt={`Company image ${imageIndex + 1}`}
                      onClick={() =>
                        toggleImageSelection({
                          url: item.fileUrl,
                          companyPhotoSeq: item.companyPhotoSeq,
                        })
                      }
                      className="object-cover w-full h-full rounded-lg cursor-pointer"
                    />
                    )}
                    {/* 대표 배지 */}
                    {isRep && (
                      <span className="absolute top-1 left-1 rounded-full bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold text-white">
                        ★대표
                      </span>
                    )}
                    {/* 컨트롤 바: 대표 지정 · 순서 */}
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between rounded-b-lg bg-black/45 px-1 py-0.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetRepresentative(item.companyPhotoSeq);
                        }}
                        className={`text-[10px] leading-none ${isRep ? "text-orange-300" : "text-white"}`}
                      >
                        {isRep ? "★" : "☆"}
                      </button>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMovePhoto(imageIndex, -1);
                          }}
                          className="text-[10px] leading-none text-white disabled:opacity-30"
                          disabled={imageIndex === 0}
                        >
                          ◀
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMovePhoto(imageIndex, 1);
                          }}
                          className="text-[10px] leading-none text-white disabled:opacity-30"
                          disabled={imageIndex === (photoListData?.data?.length ?? 0) - 1}
                        >
                          ▶
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                등록된 사업장 이미지가 없습니다.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 이미지 뷰어 팝업 */}
      {selectedImage && (
        <ImageViewer
          imageUrl={selectedImage}
          isVisible={isImageViewerVisible}
          onClose={closeImageViewer}
          onDownload={handleImageDownload}
          onDelete={handleImageDelete}
          images={photoListData?.data?.map((item) => item.fileUrl) || []}
          currentIndex={currentImageIndex}
          onImageChange={(index) => {
            setCurrentImageIndex(index);
            const allImages = photoListData?.data || [];
            if (allImages[index]) {
              setSelectedImage(allImages[index].fileUrl);
              setSelectedCompanyPhotoSeq(allImages[index].companyPhotoSeq);
              setSelectedImagesUrl(allImages[index].fileUrl);
            }
          }}
        />
      )}

      {/* 성공/실패 Dialog */}
      <Dialog visible={isDialogVisible} onClose={handleDialogClose}>
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">{dialogTitle}</h3>
          <p className="mb-4 text-gray-600">{dialogMessage}</p>
          <button
            onClick={handleDialogClose}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 삭제 확인 Dialog */}
      <Dialog
        visible={isDeleteConfirmVisible}
        onClose={handleDeleteConfirmClose}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">사진 삭제</h3>
          <p className="mb-4 text-gray-600">
            정말로 이 사진을 삭제하시겠습니까?
          </p>
          <p className="mb-4 text-gray-600">
            삭제된 사진은 복구할 수 없습니다.
          </p>
          <div className="flex gap-3">
            <button
              className="flex-1 py-2 font-semibold text-gray-700 bg-white rounded-lg border border-gray-300"
              onClick={handleDeleteConfirmClose}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={handleDeleteConfirm}
            >
              삭제
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
